import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PhaseStatus } from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { runAssetsApi } from '../../lib/runAssets';
import { runVideosApi } from '../../lib/runVideosApi';
import { useRunStore } from '../../state/runStore';
import { useVideosStore } from '../../state/videosStore';
import { SidebarTestsList } from '../tests-wizard/SidebarTestsList';
import { TestDetailPane } from '../tests-wizard/TestDetailPane';
import { useAutoAnalyze } from '../tests-wizard/useAutoAnalyze';
import { ImagesGalleryDialog } from './ImagesGalleryDialog';
import { Phase3Toolbar } from './Phase3Toolbar';

/**
 * Phase 5 dashboard panel - QAmlet validation. Reads inputs prepared by the
 * earlier phases (source from Phase 3, screenshots + analysis from Phase 4)
 * and runs the per-test AI assessment.
 *
 * The full top section is now a single sticky toolbar (Phase3Toolbar) so the
 * wizard (sidebar + test detail) takes the full remaining viewport. Image
 * actions still live in Phase 4; the only action this panel triggers is
 * `load-tests` plus the bulk "analyze all" job.
 *
 * NOTE: this panel still lives under `features/phase3/` (and the toolbar
 * file is still named `Phase3Toolbar.tsx`) for git-history reasons; the
 * visible step number changed to 5 when the rail was reordered. See
 * `App.tsx` for the URL <-> folder mapping.
 */
export function AIValidationPanel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const runId = id!;
  const {
    current,
    tests,
    criticalFiles,
    loadCriticalFiles,
    loadRun,
    setPhase,
  } = useRunStore();
  const [imagesCount, setImagesCount] = useState<number | null>(null);
  const [analysisExists, setAnalysisExists] = useState(false);
  const [videoAnalysisExists, setVideoAnalysisExists] = useState(false);
  const [reloadBusy, setReloadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filter, setFilter] = useState('');
  // Watch the videos store so the toolbar's Video chip flips to "analysed"
  // the moment the WS pushes a `video-analyze-progress` completion event
  // (or the user uploads/deletes a video on another tab).
  const videoList = useVideosStore((s) => s.list);

  useEffect(() => {
    if (!runId) return;
    runAssetsApi
      .listImages(runId)
      .then((data) => setImagesCount(data.items.length))
      .catch(() => setImagesCount(0));
    runAssetsApi
      .loadImagesAnalysis(runId)
      .then((data) => setAnalysisExists(data.exists))
      .catch(() => setAnalysisExists(false));
  }, [runId, current?.phases?.drive, current?.phases?.['analyze-images']]);

  // Drive the Video chip + Inputs toggle from the videos store; fall back
  // to a one-shot fetch when the store hasn't been loaded yet (e.g. user
  // navigated straight to Phase 7 without opening the videos page first).
  useEffect(() => {
    if (videoList.length > 0) {
      setVideoAnalysisExists(videoList.some((v) => v.analysisExists));
      return;
    }
    if (!runId) return;
    runVideosApi
      .list(runId)
      .then((data) =>
        setVideoAnalysisExists(data.items.some((v) => v.analysisExists)),
      )
      .catch(() => setVideoAnalysisExists(false));
  }, [runId, videoList]);

  useEffect(() => {
    if (current?.project_path && !criticalFiles) {
      void loadCriticalFiles(runId, 'texec').catch(() => undefined);
    }
  }, [runId, current?.project_path, criticalFiles, loadCriticalFiles]);

  const unanalyzed = tests.filter((t) => !t.ai_verdict && !t.tester_verdict).length;
  const autoAnalyze = useAutoAnalyze({ runId, totalTests: tests.length, unanalyzed });

  const reloadTests = async () => {
    setReloadBusy(true);
    setError(null);
    if (current) setPhase('load-tests', 'in_progress' as PhaseStatus);
    try {
      await api.post(`/api/runs/${runId}/load-tests`);
      await loadRun(runId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setReloadBusy(false);
    }
  };

  if (!current) return null;
  const drivePhase = current.phases?.['drive'];
  const loadTestsPhase = current.phases?.['load-tests'];
  const test = tests[currentIndex];
  const imagesReady = drivePhase === 'completed' && (imagesCount ?? 0) > 0;

  return (
    <div className="phase3-panel">
      <Phase3Toolbar
        criticalFiles={criticalFiles}
        imagesCount={imagesCount}
        imagesReady={imagesReady}
        drivePhaseFailed={drivePhase === 'failed'}
        analysisExists={analysisExists}
        videoAnalysisExists={videoAnalysisExists}
        totalTests={tests.length}
        unanalyzed={unanalyzed}
        loadTestsCompleted={loadTestsPhase === 'completed'}
        reloadBusy={reloadBusy}
        onReloadTests={() => void reloadTests()}
        onOpenGallery={() => setGalleryOpen(true)}
        onGoToImagesPhase={() => navigate(`/runs/${runId}/phase/4`)}
        onGoToVideosPage={() => navigate(`/runs/${runId}/phase/5`)}
        autoAnalyze={autoAnalyze}
      />

      {error && (
        <div className="phase3-panel-error">
          <p style={{ color: 'var(--bad)', margin: 0 }} className="small">{error}</p>
        </div>
      )}

      {tests.length > 0 ? (
        <div className="wizard">
          <SidebarTestsList
            runId={runId}
            tests={tests}
            currentIndex={currentIndex}
            onSelect={setCurrentIndex}
            filter={filter}
            onFilter={setFilter}
          />
          {test ? (
            <TestDetailPane
              runId={runId}
              test={test}
              hasPrev={currentIndex > 0}
              hasNext={currentIndex < tests.length - 1}
              onPrev={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              onNext={() => setCurrentIndex((i) => Math.min(tests.length - 1, i + 1))}
            />
          ) : (
            <div className="detail">
              <p className="muted">Select a test from the left.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <p className="muted">
            No Xray tests loaded yet. Click <strong>Load Tests</strong> in the toolbar above once the
            TEXEC has test executions.
          </p>
        </div>
      )}

      {galleryOpen && (
        <ImagesGalleryDialog runId={runId} onClose={() => setGalleryOpen(false)} />
      )}
    </div>
  );
}
